import React from "react";
import { FileText } from "lucide-react";

const HERO_IMAGE = "/assets/images/logo-landing.jpg";
const PATTERN = "/assets/images/blob.svg";

const SectionBlock = ({ number, title, children }) => (
  <section className="border-b border-slate-100 pb-8 last:border-b-0 last:pb-0">
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center font-black shrink-0">
        {number}
      </div>

      <div>
        <h3 className="text-2xl font-black text-[#06183f] mb-3">{title}</h3>
        <div className="space-y-4 text-slate-700 leading-8">{children}</div>
      </div>
    </div>
  </section>
);

const TermsPage = () => {
  return (
    <main className="bg-slate-50 overflow-hidden">
      <section
        className="relative pt-36 pb-24 bg-[#06183f] text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(6, 24, 63, 0.88), rgba(6, 24, 63, 0.94)), url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <img
          src={PATTERN}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
        />

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="w-10 h-[3px] bg-[#ff6600]" />
            <span className="text-[#ff6600] font-black tracking-[0.2em] text-sm uppercase">
              AKSUBEB Website Terms
            </span>
            <span className="w-10 h-[3px] bg-[#ff6600]" />
          </div>

          <h1
            className="text-5xl md:text-7xl font-black leading-tight"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Site Terms and Conditions
          </h1>

          <p className="text-white/80 mt-5 max-w-3xl mx-auto leading-relaxed">
            These terms govern the use of the official AKSUBEB website and its
            approved digital services.
          </p>

          <p className="mt-4 text-[#ff6600] font-bold">Effective Date: May 2026</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="bg-white rounded-[34px] p-6 md:p-10 shadow-xl border border-slate-100">
          <div className="flex flex-col md:flex-row md:items-center gap-5 mb-10 bg-slate-50 rounded-[26px] p-6 border border-slate-100">
            <div className="w-16 h-16 rounded-2xl bg-[#ff6600]/10 text-[#ff6600] flex items-center justify-center">
              <FileText size={30} />
            </div>

            <div>
              <h2 className="text-3xl font-black text-[#06183f]">
                Akwa Ibom State Universal Basic Education Board
              </h2>
              <p className="text-slate-500 mt-1">Website: subeb.ak.gov.ng</p>
            </div>
          </div>

          <div className="space-y-9">
            <div className="text-slate-700 leading-8 space-y-4">
              <p>
                Welcome to the official website of the Akwa Ibom State Universal
                Basic Education Board, AKSUBEB.
              </p>

              <p>
                By accessing or using this website, you agree to comply with
                these Site Terms and Conditions. If you do not agree with any
                part of these terms, you should discontinue use of the website.
              </p>
            </div>

            <SectionBlock number="01" title="Purpose of the Website">
              <p>
                This website provides official information about AKSUBEB, its
                mandate, programmes, projects, services, policies,
                announcements, media resources, and public education activities.
              </p>

              <p>
                The website may also provide access to online forms,
                publications, reports, school information, teacher information,
                project data, and other digital services approved by AKSUBEB.
              </p>
            </SectionBlock>

            <SectionBlock number="02" title="Use of the Website">
              <p>
                Users are expected to use this website only for lawful, official,
                educational, informational, and public service purposes.
              </p>

              <p>Users must not:</p>

              <ul className="list-disc pl-6 space-y-2">
                <li>Submit false, misleading, harmful, or unlawful information.</li>
                <li>
                  Attempt to gain unauthorised access to any part of the website,
                  server, database, or digital service.
                </li>
                <li>Upload malicious files, viruses, spam, or harmful content.</li>
                <li>
                  Interfere with the security, performance, or availability of
                  the website.
                </li>
                <li>
                  Use the website to impersonate any person, school, government
                  agency, or organisation.
                </li>
                <li>
                  Copy, alter, or misuse official records, forms, images, logos,
                  publications, or data without authorisation.
                </li>
              </ul>
            </SectionBlock>

            <SectionBlock number="03" title="Accuracy of Information">
              <p>
                AKSUBEB will make reasonable efforts to ensure that information
                published on this website is accurate, current, and useful.
              </p>

              <p>
                However, information on the website may be updated, corrected,
                replaced, or removed at any time without prior notice.
              </p>

              <p>
                Users should contact AKSUBEB for confirmation where official
                verification is required.
              </p>
            </SectionBlock>

            <SectionBlock number="04" title="Official Communications">
              <p>
                Information published on this website may include public notices,
                announcements, project updates, education guidelines, recruitment
                information, procurement notices, and other official
                communications.
              </p>

              <p>
                Users should rely only on information published through approved
                AKSUBEB channels or confirmed by authorised officers of the
                Board.
              </p>

              <p>
                AKSUBEB will not be responsible for false information circulated
                through unauthorised websites, social media pages, individuals,
                or third-party platforms.
              </p>
            </SectionBlock>

            <SectionBlock number="05" title="Intellectual Property">
              <p>
                All content on this website, including text, graphics, images,
                documents, logos, design elements, videos, downloadable files,
                and other materials, belongs to AKSUBEB or is used with proper
                authorisation.
              </p>

              <p>
                Users may view, download, or print content for personal,
                educational, research, or official reference purposes, provided
                the content is not altered, misrepresented, sold, or used for
                unlawful purposes.
              </p>

              <p>
                No content from this website may be reproduced, republished,
                distributed, or used for commercial purposes without prior
                written approval from AKSUBEB.
              </p>
            </SectionBlock>

            <SectionBlock number="06" title="User Submissions">
              <p>
                Users may submit information through online forms, feedback
                channels, applications, complaints, enquiries, or other approved
                digital services.
              </p>

              <p>
                By submitting information, users confirm that the information
                provided is accurate, lawful, and submitted in good faith.
              </p>

              <p>
                AKSUBEB reserves the right to review, verify, accept, reject,
                correct, or remove any submitted information where necessary.
              </p>
            </SectionBlock>

            <SectionBlock number="07" title="Privacy and Data Protection">
              <p>
                AKSUBEB is committed to protecting personal information
                submitted through this website.
              </p>

              <p>
                The collection, use, storage, and protection of personal
                information are governed by the AKSUBEB Privacy Statement
                published on this website.
              </p>

              <p>
                Users are encouraged to read the Privacy Statement before
                submitting personal information.
              </p>
            </SectionBlock>

            <SectionBlock number="08" title="Third-Party Links">
              <p>
                This website may contain links to other government, partner, or
                third-party websites.
              </p>

              <p>
                Such links are provided for convenience and public information
                only.
              </p>

              <p>
                AKSUBEB does not control external websites and will not be
                responsible for their content, accuracy, security, privacy
                practices, or availability.
              </p>

              <p>Users access third-party websites at their own discretion.</p>
            </SectionBlock>

            <SectionBlock number="09" title="Website Availability">
              <p>
                AKSUBEB will make reasonable efforts to keep this website
                available, secure, and functional.
              </p>

              <p>
                However, access may be interrupted due to maintenance, technical
                faults, service upgrades, internet disruptions, security reviews,
                or circumstances beyond the control of AKSUBEB.
              </p>

              <p>
                AKSUBEB will not be liable for any loss, delay, or inconvenience
                caused by temporary website unavailability.
              </p>
            </SectionBlock>

            <SectionBlock number="10" title="Limitation of Liability">
              <p>
                The information on this website is provided for general public
                information and official service support.
              </p>

              <p>
                AKSUBEB will not be liable for any loss, damage, claim, error,
                omission, delay, or inconvenience arising from the use of this
                website, reliance on its content, inability to access the
                website, or use of third-party links.
              </p>

              <p>
                Users are responsible for how they use information obtained from
                the website.
              </p>
            </SectionBlock>

            <SectionBlock number="11" title="Security">
              <p>
                Users must not attempt to breach, bypass, test, disrupt, or
                compromise the security of this website or any related system.
              </p>

              <p>
                Any unauthorised access, interference, data misuse, cyberattack,
                or attempted breach may be reported to relevant law enforcement
                or regulatory authorities.
              </p>
            </SectionBlock>

            <SectionBlock number="12" title="Changes to These Terms">
              <p>
                AKSUBEB may update these Site Terms and Conditions from time to
                time to reflect changes in law, technology, operations, public
                service requirements, or website functions.
              </p>

              <p>
                Updated terms will be published on this website with a revised
                effective date.
              </p>

              <p>
                Continued use of the website after any update means the user
                accepts the revised terms.
              </p>
            </SectionBlock>

            <SectionBlock number="13" title="Governing Law">
              <p>
                These Site Terms and Conditions are governed by the laws of the
                Federal Republic of Nigeria and applicable laws of Akwa Ibom
                State.
              </p>

              <p>
                Any dispute arising from the use of this website shall be handled
                in accordance with applicable Nigerian law.
              </p>
            </SectionBlock>

            <SectionBlock number="14" title="Contact">
              <p>
                For questions, enquiries, complaints, or requests relating to
                these Site Terms and Conditions, please contact:
              </p>

              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                <p className="font-bold text-[#06183f]">
                  Akwa Ibom State Universal Basic Education Board
                </p>
                <p>Uyo, Akwa Ibom State, Nigeria</p>
                <p>Website: subeb.ak.gov.ng</p>
                <p>Email: info@subeb.ak.gov.ng</p>
              </div>
            </SectionBlock>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TermsPage;